/**
 * Node type registration for switchboard canvas
 */
import { NodeProps, NodeTypes } from '@xyflow/react'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'
import type { MessageGroup } from '@mangovoice/types'

import {
  AiAgentV1Node,
  AutoAttendantNode,
  CallDistributionNode,
  ChangeCallerIdNode,
  ConferenceRoomNode,
  DayNightNode,
  DefaultNode,
  DialByNameNode,
  ExternalTransferNode,
  FaxDetectNode,
  HangUpNode,
  InteractiveWebhookNode,
  JumpToSwitchboardNode,
  LeaveVoicemailNode,
  QueueNode,
  ScheduleNode,
  SmsNotificationNode,
  SoundClipNode,
  StartNode,
  TransferAndContinueNode,
  TransferAndRolloverNode,
  TransferNode,
  WaitNode,
  WebhookNode,
} from '../nodes'

/**
 * Helper to create a node component with props
 */
function createNodeComponent<T extends Record<string, unknown>>(
  Component: React.ComponentType<NodeProps & T>,
  props: T,
  onDeleteNode?: (nodeId: string) => void,
): React.ComponentType<NodeProps> {
  return (nodeProps: NodeProps) => (
    <Component
      {...nodeProps}
      {...props}
      onDeleteNode={onDeleteNode}
      id={nodeProps.id}
    />
  )
}

/**
 * Helper to create node component with extensionMap
 * Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
 */
function createExtensionNode(
  Component: React.ComponentType<
    NodeProps & {
      extensionMap?: Map<number, ExtensionInstance>
      onDeleteNode?: (nodeId: string) => void
      id?: string
    }
  >,
  extensionMap?: Map<number, ExtensionInstance>,
  _extensionsList?: ExtensionInstance[], // Kept for backward compatibility but not passed
  onDeleteNode?: (nodeId: string) => void,
) {
  return createNodeComponent(Component, { extensionMap }, onDeleteNode)
}

/**
 * Helper to create node component with sound clips
 * Note: soundClips is provided via Jotai atoms (soundClipsAtom)
 */
function createSoundClipNode(
  Component: React.ComponentType<
    NodeProps & {
      onDeleteNode?: (nodeId: string) => void
      id?: string
    }
  >,
  _soundClips?: Array<{ id: string; name: string }>, // Kept for backward compatibility but not passed
  onDeleteNode?: (nodeId: string) => void,
) {
  return createNodeComponent(Component, {}, onDeleteNode)
}

/**
 * Helper to create node component with schedules
 * Note: schedules is provided via Jotai atoms (schedulesAtom)
 */
function createScheduleNode(
  Component: React.ComponentType<
    NodeProps & {
      onDeleteNode?: (nodeId: string) => void
      id?: string
    }
  >,
  _schedules?: Array<{ id: string; name: string }>, // Kept for backward compatibility but not passed
  onDeleteNode?: (nodeId: string) => void,
) {
  return createNodeComponent(Component, {}, onDeleteNode)
}

/**
 * Helper to create node component with messaging groups
 */
function createMessagingNode(
  Component: React.ComponentType<
    NodeProps & {
      messagingGroups?: MessageGroup[]
      onDeleteNode?: (nodeId: string) => void
      id?: string
    }
  >,
  messagingGroups?: MessageGroup[],
  onDeleteNode?: (nodeId: string) => void,
) {
  return createNodeComponent(Component, { messagingGroups }, onDeleteNode)
}

/**
 * Helper to create node component with phone numbers
 */
function createPhoneNumberNode(
  Component: React.ComponentType<
    NodeProps & {
      phoneNumbers?: Array<{ id: string; name: string; number: string }>
      onDeleteNode?: (nodeId: string) => void
      id?: string
    }
  >,
  phoneNumbers?: Array<{ id: string; name: string; number: string }>,
  onDeleteNode?: (nodeId: string) => void,
) {
  return createNodeComponent(Component, { phoneNumbers }, onDeleteNode)
}

/**
 * Helper to create node component with extensions and sound clips
 * Note: extensionsList and soundClips are provided via Jotai atoms
 */
function createExtensionAndSoundClipNode(
  Component: React.ComponentType<
    NodeProps & {
      extensionMap?: Map<number, ExtensionInstance>
      onDeleteNode?: (nodeId: string) => void
      id?: string
    }
  >,
  extensionMap?: Map<number, ExtensionInstance>,
  _extensionsList?: ExtensionInstance[], // Kept for backward compatibility but not passed
  _soundClips?: Array<{ id: string; name: string }>, // Kept for backward compatibility but not passed
  onDeleteNode?: (nodeId: string) => void,
) {
  return createNodeComponent(
    Component,
    {
      extensionMap,
    },
    onDeleteNode,
  )
}

/**
 * Register node types for React Flow
 *
 * @param extensionMap - Map of extension IDs to ExtensionInstance
 * @param extensionsList - List of extensions for dropdowns
 * @param messagingGroups - List of messaging groups for SMS notification nodes
 * @param soundClips - List of sound clips for Sound Clip nodes
 * @param schedules - List of schedules for Schedule nodes
 * @param phoneNumbers - List of phone numbers for External Transfer caller ID
 * @param onDeleteNode - Optional callback for node deletion
 * @returns NodeTypes configuration for React Flow
 */
export function getNodeTypes(
  extensionMap?: Map<number, ExtensionInstance>,
  extensionsList?: ExtensionInstance[],
  messagingGroups?: MessageGroup[],
  soundClips?: Array<{ id: string; name: string }>,
  schedules?: Array<{ id: string; name: string }>,
  phoneNumbers?: Array<{ id: string; name: string; number: string }>,
  onDeleteNode?: (nodeId: string) => void,
): NodeTypes {
  // Helper to inject onDeleteNode and id into simple node components
  const withDeleteHandler = (
    Component: React.ComponentType<
      NodeProps & { onDeleteNode?: (nodeId: string) => void; id?: string }
    >,
  ): React.ComponentType<NodeProps> => {
    return (props: NodeProps) => (
      <Component {...props} onDeleteNode={onDeleteNode} id={props.id} />
    )
  }

  return {
    default: withDeleteHandler(DefaultNode),
    transfer: createExtensionNode(
      TransferNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ),
    transfer_and_continue: createExtensionNode(
      TransferAndContinueNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ),
    transfer_and_rollover: createExtensionNode(
      TransferAndRolloverNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ),
    queue: createExtensionNode(
      QueueNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ),
    fax_detect: createExtensionNode(
      FaxDetectNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ),
    auto_attendant: createSoundClipNode(
      AutoAttendantNode,
      soundClips,
      onDeleteNode,
    ),
    webhook: withDeleteHandler(WebhookNode),
    interactive_webhook: withDeleteHandler(InteractiveWebhookNode),
    external_transfer: createPhoneNumberNode(
      ExternalTransferNode,
      phoneNumbers,
      onDeleteNode,
    ),
    external_number: createPhoneNumberNode(
      ExternalTransferNode,
      phoneNumbers,
      onDeleteNode,
    ), // Legacy alias
    hang_up: withDeleteHandler(HangUpNode),
    leave_voicemail: createExtensionAndSoundClipNode(
      LeaveVoicemailNode,
      extensionMap,
      extensionsList,
      soundClips,
      onDeleteNode,
    ),
    wait: withDeleteHandler(WaitNode),
    sms_notification: createMessagingNode(
      SmsNotificationNode,
      messagingGroups,
      onDeleteNode,
    ),
    change_caller_id: withDeleteHandler(ChangeCallerIdNode),
    sound_clip: createSoundClipNode(SoundClipNode, soundClips, onDeleteNode),
    dial_by_name: withDeleteHandler(DialByNameNode),
    day_night: withDeleteHandler(DayNightNode),
    schedule: createScheduleNode(ScheduleNode, schedules, onDeleteNode),
    conference_room: createExtensionNode(
      ConferenceRoomNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ),
    call_distribution: withDeleteHandler(CallDistributionNode),
    jump_to_switchboard: createExtensionNode(
      JumpToSwitchboardNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ),
    jump_to_dial_plan: createExtensionNode(
      JumpToSwitchboardNode,
      extensionMap,
      extensionsList,
      onDeleteNode,
    ), // Legacy alias
    start: withDeleteHandler(StartNode), // Special node for start_node from old format
    ai_agent_v1: withDeleteHandler(AiAgentV1Node),
  }
}
